import { Router } from 'vue-router'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import { exitManager } from './exitmanager'
import { useUserStore } from 'd3components'
// 负责统一管理应用的导航行为，包括硬件返回键和手势返回 集成了滑动退出功能
class NavigationManager {
  private router: Router | null = null
  // 路由历史栈（用于兜底返回；主要仍依赖 router.back()）
  private historyStack: string[] = []
  private swipeExitEnabled = true

  // “动态根页”：首次进入的业务 /page/* 页面（用于根页触发退出确认）
  private dynamicRootPath: string | null = null

  // 防抖：避免短时间内 back 触发两次（滑动/硬件返回可能叠加）
  private lastBackAt = 0
  private readonly BACK_GUARD_MS = 250

  // /page/* 下的工具/独立页：不参与“动态根页”判定
  private readonly EXCLUDED_DYNAMIC_ROOT_PATHS = new Set<string>(['/page/logs','/page/pass','page/uploadAvatar'])

  private isExcludedDynamicRootPath(path: string): boolean {
    return this.EXCLUDED_DYNAMIC_ROOT_PATHS.has(path)
  }

  private isPagePath(path: string): boolean {
    return path.startsWith('/page/')
  }

  private isDynamicInjectedRoute(route: any): boolean {
    try {
      if (!route) return false
      // Vue Router 4: 动态路由 meta 可能只存在于 matched 的某些 record 上
      if (Array.isArray(route.matched) && route.matched.some((r: any) => r?.meta?._isDynamic)) return true
      return !!(route.meta && (route.meta as any)._isDynamic)
    } catch {
      return false
    }
  }

  private isGroupRoute(route: any): boolean {
    try {
      return !!(route && route.meta && (route.meta as any)._isGroup)
    } catch {
      return false
    }
  }

  private getPagePid(path: string): string {
    return this.isPagePath(path) ? path.slice('/page/'.length) : ''
  }

  private hasMenuList(): boolean {
    try {
      const userStore = useUserStore()
      return Array.isArray((userStore as any).routerData) && (userStore as any).routerData.length > 0
    } catch {
      return false
    }
  }

  // 把“真实业务菜单页”视为动态根候选页。
  // menuList 未就绪时：先放行，等 menuList 就绪后会自动排除不合法的 dynamicRootPath。
  private isMenuRoute(route: any): boolean {
    const path = String(route?.path || '')
    if (!path) return false
    if (this.isExcludedDynamicRootPath(path)) return false

    // 动态注入路由（resetMenuToRouter 注入的菜单路由）直接视为菜单页
    if (this.isDynamicInjectedRoute(route)) return true

    // 兼容旧的 /page/* 结构
    if (!this.isPagePath(path)) return false
    const pid = this.getPagePid(path)
    const topPid = this.getTopMenuPid(pid)
    return topPid !== null || !this.hasMenuList()
  }

  // 初始化导航管理器
  init(router: Router) {
    this.router = router
    this.setupBackButtonHandler()
    this.trackNavigation()
  }
  // 原生平台：监听硬件返回键，只注册一次
  private backHandler: Promise<PluginListenerHandle> | null = null

  private setupBackButtonHandler() {
    if (!Capacitor.isNativePlatform()) return
    if (this.backHandler) return

    this.backHandler = App.addListener('backButton', () => {
      if (this.swipeExitEnabled) this.handleBack('hardware')
    })
  }

  
  // 获取某个 pid 对应的顶级菜单 pid
  // @param pid 页面 pid
  // @returns 顶级菜单 pid，如果找不到则返回 null
  private getTopMenuPid(pid: string): string | null {
    try {
      const userStore = useUserStore()
      const menuList = (userStore as any).routerData || []
      
      for (const topMenu of menuList) {
        const topMenuAny = topMenu as any
        // 检查是否是顶级菜单自身
        if (topMenuAny?.pid === pid) {
          return pid
        }
        // 检查是否是子菜单
        const subs = topMenuAny?.subs || []
        if (Array.isArray(subs) && subs.some((sub: any) => sub?.pid === pid)) {
          return topMenuAny.pid
        }
      }
    } catch (error) {
      console.warn('[NavigationManager] 获取顶级菜单失败', error)
    }
    return null
  }

  // 记录路由历史 +  dynamicRootPath
  private trackNavigation() {
    if (!this.router) return
    this.router.beforeEach((to, from) => {
      // history：不记录 login / 根路径，且排除无变化跳转
      if (from.path !== to.path && from.path !== '/' && from.path !== '/login') {
        this.historyStack.push(from.path)
        if (this.historyStack.length > 3) this.historyStack.shift()
      }

      // menuList 就绪后，如果 dynamicRootPath 不是菜单页（例如 /page/logs），则清掉
      if (this.dynamicRootPath) {
        try {
          const resolved = this.router?.resolve(this.dynamicRootPath)
          if (!this.isMenuRoute(resolved)) {
            console.log(`[NavigationManager] clear dynamicRootPath (not menu route): ${this.dynamicRootPath}`)
            this.dynamicRootPath = null
          }
        } catch {
          console.log(`[NavigationManager] clear dynamicRootPath (resolve failed): ${this.dynamicRootPath}`)
          this.dynamicRootPath = null
        }
      }

      // 从“非菜单页”进入“菜单页”时，将目标页标记为动态根页
      if (this.isMenuRoute(to) && !this.isMenuRoute(from)) {
        this.dynamicRootPath = String(to.path)
        console.log(`[NavigationManager] set dynamicRootPath: ${this.dynamicRootPath} (from: ${String(from.path)})`)
      }

      // 首屏/首次进入时，可能先进入“分组路由”(PageTabs 容器) 再自动切到第一个子页面。
      // 这时 root 应该是“实际展示的叶子页”，而不是分组容器路径。
      if (
        this.dynamicRootPath &&
        this.dynamicRootPath === String(from.path) &&
        this.isGroupRoute(from) &&
        this.isMenuRoute(to) &&
        !this.isGroupRoute(to)
      ) {
        this.dynamicRootPath = String(to.path)
        console.log(`[NavigationManager] promote dynamicRootPath to leaf: ${this.dynamicRootPath}`)
      }
    })
  }

  // 重置动态根页面
  // 在用户重新登录或需要重置时调用
  resetDynamicRootPage() {
    this.dynamicRootPath = null
    // console.log(`[NavigationManager] Dynamic root page reset`)
  }
  
  // 处理返回操作的核心逻辑
  // 根据当前页面和导航历史决定返回行为
  // 现在集成了滑动退出功能
  handleBack(source: 'hardware' | 'swipe' = 'hardware', isQualifiedExitGesture: boolean = true) {
    if (!this.router) return

    const now = Date.now()
    if (now - this.lastBackAt < this.BACK_GUARD_MS) return
    this.lastBackAt = now

    const currentRoute = this.router.currentRoute.value.path
    // console.log(`[NavigationManager] handleBack 被调用, source: ${source}, currentRoute: ${currentRoute}`)

    // 如果是根页面，处理退出逻辑
    if (this.isRootPage(currentRoute)) {
      // console.log(`[NavigationManager] 识别为根页面，调用 handleRootPageBack`)
      this.handleRootPageBack(source, isQualifiedExitGesture)
      return
    }

    // 特殊页面处理
    // if (this.isSpecialPage(currentRoute)) {
    //   this.handleSpecialPageBack(currentRoute)
    //   return
    // }

    // 普通页面返回逻辑
    if (this.historyStack.length > 0) {
      // 有导航历史，返回上一页
      this.historyStack.pop()
      this.router.back()
    } else {
      // 超过最大回退次数后（historyStack 为空），滑动返回直接进入退出流程
      if (source === 'swipe') {
        exitManager.handleExitIntent('swipe', isQualifiedExitGesture)
        return
      }

      // 没有历史：
      // - 已登录：回到根路径，让 permission.ts 基于 userMenuRouter 分发到第一个可达页面
      // - 未登录：回到登录页
      let authed = false
      try {
        const userStore = useUserStore()
        const auth = (userStore as any)?.auth
        authed = !!(auth && typeof auth === 'object' && Object.keys(auth).length > 0)
      } catch {
        authed = false
      }

      if (authed) {
        this.router.replace('/')
      } else {
        this.router.replace('/login')
      }
    }
  }

  // 处理根页面的返回
  private handleRootPageBack(source: 'hardware' | 'swipe', isQualifiedExitGesture: boolean = true) {
    const currentRoute = this.router!.currentRoute.value.path
    console.log(`[NavigationManager] handleRootPageBack 被调用, currentRoute: ${currentRoute}, dynamicRootPath: ${this.dynamicRootPath}`)

    // 根页面统一走“退出确认”逻辑
    exitManager.handleExitIntent(source, isQualifiedExitGesture)
  }
  

  // 判断是否为根页面
  private isRootPage(path: string): boolean {
    // const fixedRootPaths = new Set<string>(['/member', '/login'])
    const fixedRootPaths = new Set<string>(['/login'])
    const isDynamicRoot = this.dynamicRootPath !== null && path === this.dynamicRootPath
    const isRoot = fixedRootPaths.has(path) || isDynamicRoot
    console.log(`[NavigationManager] Checking if ${path} is root page: ${isRoot} (isDynamicRoot: ${isDynamicRoot}, dynamicRootPath: ${this.dynamicRootPath})`)
    return isRoot
  }
  
  // 判断是否为特殊页面（需要特殊返回处理）
  // private isSpecialPage(path: string): boolean {
  //   const specialPages = ['/pass', '/uploadAvatar']
  //   return specialPages.includes(path)
  // }
  
  // 处理特殊页面的返回逻辑
  // private handleSpecialPageBack(path: string) {
    // 现在根据xfont配置的member页面，所以先注释掉这部分
    // switch (path) {
    //   case '/pass':
    //   case '/uploadAvatar':
    //     // 个人中心相关页面返回到个人中心
    //     this.router!.push('/member')
    //     break
    //   default:
    //     this.router!.back()
    // }
  // }
  
  // 手动触发返回（供滑动手势调用）
  // 提供给滑动返回组件使用的公共接口
  triggerBack(isQualifiedExitGesture: boolean = true) {
    this.handleBack('swipe', isQualifiedExitGesture)
  }

  // 手动触发硬件返回键
  triggerHardwareBack() {
    this.handleBack('hardware')
  }

  // 启用滑动退出功能
  enableSwipeExit() {
    this.swipeExitEnabled = true
    exitManager.enable()
    // console.log('Swipe exit enabled')
  }

  // 禁用滑动退出功能
  disableSwipeExit() {
    this.swipeExitEnabled = false
    exitManager.disable()
    // console.log('Swipe exit disabled')
  }

  // 检查滑动退出是否启用
  isSwipeExitEnabled(): boolean {
    return this.swipeExitEnabled
  }

  // 强制退出应用（无确认）
  forceExit() {
    exitManager.forceExit()
  }

  // 检查是否有退出意图
  hasExitIntent(): boolean {
    return exitManager.hasExitIntent()
  }
  
  // 清理导航历史
  // 用于登录/登出时重置导航状态
  clearHistory() {
    this.historyStack = []
  }
  
  // 获取当前导航历史长度
  // 用于调试和状态检查
  getHistoryLength(): number {
    return this.historyStack.length
  }
}

// 导出单例实例
export const navigationManager = new NavigationManager()
